import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { bugtracker, useIdentityContext } from '@99designs/design-services-common';
import { FullPageSpinner } from '@99designs/design-services-layouts';
import {
    ReviewForm,
    useBriefContext,
    useBriefQuery,
    useUpdateProductOnBriefChange,
} from '@99designs/feature-design-services-brief';
import { getApolloErrorType } from '@99designs/graph-utils';
import { __, getCurrentCulture } from '@99designs/i18n';
import { useSendPageViewOnce } from '@99designs/tracking';
import { ErrorPage, NotFoundErrorPage, PermissionErrorPage } from '../Error';

const TRACKING_CALLER = 'brief-review-unauthenticated';

export const Review: React.FC = () => {
    const { jobId } = useParams();
    const briefId = jobId as string;
    const locale = useMemo(() => getCurrentCulture(), []);
    useSendPageViewOnce(locale);
    const { SignIn, isSignedIn } = useIdentityContext();
    const {
        config: {
            tenant: { name },
        },
        onCompleted,
    } = useBriefContext();

    const { data, loading, error } = useBriefQuery({
        variables: {
            id: briefId,
        },
        onCompleted,
    });

    useUpdateProductOnBriefChange(data?.brief);

    if (error) {
        const errorType = getApolloErrorType(error);
        if (errorType === 'UNAUTHENTICATED') {
            if (!isSignedIn) {
                SignIn(undefined, { trackingCaller: TRACKING_CALLER });
                return <FullPageSpinner />;
            }

            return (
                <ErrorPage
                    title={__(
                        'There was an error loading your brief. Please refresh the page or try again later.'
                    )}
                    error={
                        new Error(
                            `User is logged in on Vistaprint but has no 99designs session: ${error.message}`
                        )
                    }
                    metadata={{ briefId }}
                />
            );
        }

        if (errorType === 'PERMISSION_DENIED') {
            return <PermissionErrorPage />;
        }

        if (errorType === 'NOT_FOUND') {
            return <NotFoundErrorPage />;
        }

        if (errorType === 'UNKNOWN_ERROR') {
            return (
                <ErrorPage
                    error={new Error(`Unknown error loading brief: ${error.message}`)}
                    metadata={{ briefId }}
                />
            );
        }

        return <ErrorPage error={error} />;
    }

    if (loading || !data) {
        return <FullPageSpinner />;
    }

    return (
        <>
            <Helmet>
                <title>
                    {__('Design Services Brief Review')} | {name}
                </title>
            </Helmet>
            <ReviewForm brief={data.brief} />
        </>
    );
};
